/**
 * InstallPrompt — nudges portal users to "Add to Home Screen".
 *
 * State machine:
 *   1. Runs `matchMedia("standalone")` and `navigator.standalone`. If either
 *      is true, we're already installed → fire POST /pwa/installed once and
 *      return null.
 *   2. Otherwise ask GET /pwa/status. If should_prompt=false (cool-off
 *      window) return null.
 *   3. If `beforeinstallprompt` event fires (Android Chrome / Edge) → show
 *      the "one-tap install" card. Tap → prompt.prompt() → mark installed.
 *   4. If on iOS Safari → show the "Tap Share → Add to Home Screen" card
 *      with an animated arrow to the Share icon.
 *   5. First visit (is_first_time from the server) → show a slightly
 *      bigger welcome variant explaining WHY, before the install CTA.
 *
 * The prompt is dismissable. Dismiss → POST /pwa/dismissed → server bumps
 * the cool-off counter (3 → 7 → 30 days).
 *
 * The whole thing renders as a bottom sheet on mobile (fixed to the
 * viewport, above the bottom-nav) and as a top-right card on desktop.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { X, Home, Share, Plus, Loader2, Smartphone } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function isStandalone() {
    try {
        if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
        // iOS Safari uses navigator.standalone (not part of the CSSOM query).
        if (window.navigator?.standalone === true) return true;
    } catch { /* ignore */ }
    return false;
}
function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isSafari() {
    const ua = navigator.userAgent;
    return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

export default function InstallPrompt() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [showing, setShowing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [beforeEvent, setBeforeEvent] = useState(null);
    const markedInstalledRef = useRef(false);

    // Only portal (client) users see this. Firm staff never do.
    const isPortalUser = user?.role === "client";

    // 1. If already installed, tell the server once per session.
    useEffect(() => {
        if (!isPortalUser) return;
        if (!isStandalone()) return;
        if (markedInstalledRef.current) return;
        markedInstalledRef.current = true;
        api.post("/portal/pwa/installed").catch(() => {});
    }, [isPortalUser]);

    // 2. Ask server whether we should even try.
    useEffect(() => {
        if (!isPortalUser) return;
        if (isStandalone()) return;
        api.get("/portal/pwa/status")
            .then((r) => {
                setStatus(r.data);
                setShowing(!!r.data?.should_prompt);
            })
            .catch(() => setStatus(null));
    }, [isPortalUser]);

    // 3. Android Chrome fires beforeinstallprompt when the site is eligible.
    useEffect(() => {
        function onBefore(e) {
            e.preventDefault();
            setBeforeEvent(e);
        }
        function onInstalled() {
            markedInstalledRef.current = true;
            api.post("/portal/pwa/installed").catch(() => {});
            setShowing(false);
        }
        window.addEventListener("beforeinstallprompt", onBefore);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBefore);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const dismiss = useCallback(async () => {
        setShowing(false);
        try { await api.post("/portal/pwa/dismissed"); } catch { /* ignore */ }
    }, []);

    const install = useCallback(async () => {
        if (!beforeEvent) return;
        setBusy(true);
        try {
            beforeEvent.prompt();
            const choice = await beforeEvent.userChoice;
            if (choice?.outcome === "accepted") {
                // The `appinstalled` listener will mark server + hide card.
                setShowing(false);
            }
        } catch { /* ignore */ }
        finally {
            setBusy(false);
            setBeforeEvent(null);
        }
    }, [beforeEvent]);

    if (!isPortalUser) return null;
    if (isStandalone()) return null;
    if (!showing) return null;

    const iOS = isIOS() && isSafari();
    const isFirst = !!status?.is_first_time;
    const canNativePrompt = !!beforeEvent;

    // ---------- Content variants ------------------------------------------
    let title, body, icon = Home, action = null;
    if (canNativePrompt) {
        title = "Install ClientOps on your phone";
        body = "Get one-tap access, notifications, and no more hunting through browser tabs.";
        icon = Smartphone;
        action = (
            <button
                type="button"
                onClick={install}
                disabled={busy}
                className="rounded-full bg-emerald-600 text-white px-4 py-2 text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                data-testid="install-prompt-install"
            >
                {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Installing…</> : <><Plus className="h-3.5 w-3.5" /> Install app</>}
            </button>
        );
    } else if (iOS) {
        title = isFirst ? "Welcome — save this to your Home Screen" : "Save ClientOps to your Home Screen";
        body = "Tap the Share icon in Safari, then choose \"Add to Home Screen\". Takes 3 seconds.";
        icon = Share;
    } else {
        // Desktop or other browsers — quiet hint, no native prompt available.
        title = "Install ClientOps for one-tap access";
        body = "In your browser menu, choose \"Install app\" or \"Add to Home screen\".";
        icon = Home;
    }

    const Icon = icon;

    return (
        <div
            className="fixed z-50 bottom-16 sm:bottom-4 inset-x-3 sm:inset-x-auto sm:right-4 sm:max-w-sm rounded-2xl bg-white border border-[#E4E4E7] shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-6 duration-300"
            data-testid="install-prompt"
        >
            {/* Header */}
            <div className="relative">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-4 pr-11">
                    <div className="flex items-start gap-3">
                        <div className="grid h-10 w-10 place-items-center rounded-full bg-white/95 text-emerald-700 shrink-0">
                            <Icon className="h-5 w-5" strokeWidth={2.2} />
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-semibold leading-tight">{title}</div>
                            <p className="mt-1 text-[12.5px] text-white/90 leading-relaxed">{body}</p>
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={dismiss}
                    className="absolute top-2 right-2 grid h-7 w-7 place-items-center rounded-full text-white/80 hover:text-white hover:bg-white/15"
                    aria-label="Dismiss"
                    data-testid="install-prompt-dismiss"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>

            {/* Content variant */}
            {iOS && (
                <div className="p-4 bg-white text-sm">
                    <ol className="space-y-2 text-[13px] text-[#0F1115]">
                        <li className="flex items-start gap-2.5">
                            <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold shrink-0 mt-0.5">1</span>
                            <span>Tap the <span className="inline-flex items-center gap-1 font-medium"><Share className="h-3.5 w-3.5" /> Share icon</span> at the bottom of Safari.</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                            <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold shrink-0 mt-0.5">2</span>
                            <span>Scroll down &amp; tap <strong>Add to Home Screen</strong>.</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                            <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold shrink-0 mt-0.5">3</span>
                            <span>Tap <strong>Add</strong>. Done — open us any time from your home screen.</span>
                        </li>
                    </ol>
                    <button
                        type="button"
                        onClick={dismiss}
                        className="mt-3 w-full rounded-lg border border-[#E4E4E7] py-2 text-xs text-[#5E636E] hover:bg-[#FAFAFA]"
                        data-testid="install-prompt-later"
                    >
                        Not now
                    </button>
                </div>
            )}

            {canNativePrompt && (
                <div className="p-3 bg-white flex items-center justify-between gap-2">
                    <button
                        type="button"
                        onClick={dismiss}
                        className="text-xs text-[#5E636E] hover:text-[#0F1115] px-2 py-1.5"
                        data-testid="install-prompt-later"
                    >
                        Not now
                    </button>
                    {action}
                </div>
            )}

            {!iOS && !canNativePrompt && (
                <div className="p-3 bg-white text-right">
                    <button
                        type="button"
                        onClick={dismiss}
                        className="text-xs text-[#5E636E] hover:text-[#0F1115] px-3 py-1.5"
                        data-testid="install-prompt-later"
                    >
                        Got it
                    </button>
                </div>
            )}
        </div>
    );
}
